<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ list?.id ? 'Edit List' : 'New List' }}
    </h2>

    <form @submit.prevent="save" class="space-y-2">
      <UiControl id="list-name" label="Name" :errors="fieldErrors.name">
        <UiInput id="list-name" v-model="form.name" placeholder="Universal — Free / DIY" />
      </UiControl>

      <UiControl id="list-description" label="Description" :errors="fieldErrors.description">
        <UiTextarea id="list-description" v-model="form.description" placeholder="Generic intent filter" />
      </UiControl>

      <UiControl
        v-if="form.scope === 'admin'"
        id="list-vertical" label="Vertical"
        tooltip-text="Industry this list targets. 'Universal' applies to all verticals."
        :errors="fieldErrors.vertical || []">
        <UiSelect id="list-vertical" v-model="form.vertical" :options="verticalOptions" />
      </UiControl>

      <UiControl
        v-if="form.scope === 'admin'"
        id="list-purpose" label="Purpose"
        tooltip-text="What kind of negatives this list contains — used to organize the library."
        :errors="fieldErrors.purpose || []">
        <UiSelect id="list-purpose" v-model="form.purpose" :options="purposeOptions" />
      </UiControl>

      <UiControl id="list-default-match-type" label="Default Match Type"
        tooltip-text="Used as the default for new keywords added to this list. Existing keywords keep their own match type."
        :errors="fieldErrors.default_match_type">
        <UiSelect id="list-default-match-type" v-model="form.default_match_type" :options="matchTypeOptions" />
      </UiControl>

      <div v-if="error" class="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ error }}
        </p>
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="$emit('on-close-panel')">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiTextarea from '~/javascript/components/ui/form/textarea/textarea.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const props = defineProps({
  list: { type: Object, default: null },
  csrfToken: { type: String, required: true },
  createUrl: { type: String, required: true },
  updateUrlBase: { type: String, required: true },
  scope: { type: String, default: 'admin' }, // 'admin' | 'account' | 'business' | 'location'
  accountId: { type: String, default: null },
  businessId: { type: String, default: null },
  locationId: { type: String, default: null }
})

const emit = defineEmits(['saved', 'on-close-panel'])

const titleize = (s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const toOptions = (values) => values.map((v) => ({ id: v, title: titleize(v) }))

const verticalOptions = toOptions([
  'universal', 'hvac', 'plumbing', 'roofing', 'pest_control',
  'law_firm', 'electrical', 'landscaping', 'other'
])
const purposeOptions = toOptions([
  'generic_intent', 'industry_specific', 'service_exclusion',
  'jobs_filter', 'geo_filter', 'brand_competitors', 'misc'
])
const matchTypeOptions = toOptions(['broad', 'phrase', 'exact'])

const form = reactive({
  name: props.list?.name || '',
  description: props.list?.description || '',
  scope: props.list?.scope || props.scope,
  vertical: props.list?.vertical || (props.scope === 'admin' ? 'universal' : null),
  purpose: props.list?.purpose || (props.scope === 'admin' ? 'generic_intent' : null),
  default_match_type: props.list?.default_match_type || 'broad',
  account_id: props.accountId,
  business_id: props.businessId,
  location_id: props.locationId
})

const saving = ref(false)
const error = ref(null)
const fieldErrors = ref({})

const buildFieldErrors = (errors) => {
  const grouped = {}
  errors.forEach((message) => {
    const lower = message.toLowerCase()
    if (lower.startsWith('name')) (grouped.name = grouped.name || []).push(message)
    else if (lower.startsWith('description')) (grouped.description = grouped.description || []).push(message)
    else if (lower.startsWith('vertical')) (grouped.vertical = grouped.vertical || []).push(message)
    else if (lower.startsWith('purpose')) (grouped.purpose = grouped.purpose || []).push(message)
    else if (lower.startsWith('default match type')) (grouped.default_match_type = grouped.default_match_type || []).push(message)
  })
  return grouped
}

const save = async() => {
  saving.value = true
  error.value = null
  fieldErrors.value = {}
  const url = props.list?.id ? `${props.updateUrlBase}/${props.list.id}` : props.createUrl
  const method = props.list?.id ? 'PATCH' : 'POST'
  const isEdit = !!props.list?.id
  // UpdateListOperation rejects scope/FK fields. Only send mutable attrs on update.
  // On create, include the scope and the FK that matches it (if any).
  const updateAttrs = {
    name: form.name,
    description: form.description,
    vertical: form.vertical,
    purpose: form.purpose,
    default_match_type: form.default_match_type
  }

  const createAttrs = {
    ...updateAttrs,
    scope: form.scope
  }
  if (form.scope === 'account') createAttrs.account_id = form.account_id
  if (form.scope === 'business') createAttrs.business_id = form.business_id
  if (form.scope === 'location') createAttrs.location_id = form.location_id

  const payload = isEdit ? updateAttrs : createAttrs
  try {
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': props.csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ negative_keyword_list: payload })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      const errors = data.errors || ['Save failed']
      fieldErrors.value = buildFieldErrors(errors)
      error.value = errors.join(', ')
      toastError(new Error(error.value))
      return
    }
    const data = await res.json()
    toastSuccess(isEdit ? `Updated "${data.list.name}"` : `Created "${data.list.name}"`)
    emit('saved', data.list)
  } catch (e) {
    error.value = e.message || 'Save failed'
    toastError(e)
  } finally {
    saving.value = false
  }
}
</script>
